import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';

import { cn } from '@lyearn/style';
import { Avatar, Box, Stack, Typography } from '@lyearn/ui';
import useLearners from '@/modules/corporate/modules/learners/hooks/useLearners';
import { UserStatusEnum } from '@/types/schema';

import { $createMentionNode } from '../../nodes/MentionNode';

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const AtSignMentionsRegex = new RegExp('(^|\\s|\\()(@([^\\s]*))$');

const AtSignMentionsRegexAliasRegex = new RegExp('(^|\\s|\\()(@([^\\s]*))$');

const SUGGESTION_LIST_LENGTH_LIMIT = 5;

type MentionUserType = {
  id: string;
  name?: string | null;
  emailId?: string | null;
  profilePicture?: string | null;
  status: UserStatusEnum;
  uniqueId: string;
  isStaff: boolean;
  staffRoleIds?: string[] | null;
  staffRoles?: Array<{ id: string; name: string }> | null;
  createdAt: string;
};

function useMentionLookupService(mentionString: string | null) {
  const [results, setResults] = useState<MentionUserType[]>([]);

  const { learners } = useLearners({
    filters: {
      searchText: mentionString,
      status: [UserStatusEnum.Active],
    },
    pause: !mentionString?.length,
  });

  useEffect(() => {
    if (!mentionString) {
      setResults([]);
      return;
    }

    if (learners) {
      // Map the learners to our simplified MentionUserType
      const mentionUsers: MentionUserType[] = Array.isArray(learners)
        ? learners.map((user) => ({
            id: user.id,
            name: user.name,
            emailId: user.emailId,
            profilePicture: user.profilePicture,
            status: user.status,
            uniqueId: user.uniqueId,
            isStaff: user.isStaff,
            staffRoleIds: user.staffRoleIds,
            staffRoles: user.staffRoles,
            createdAt: user.createdAt,
          }))
        : [];
      setResults(mentionUsers);
    }
  }, [mentionString, learners]);

  return results;
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
    return null;
  }
  // ignores leading whitespace but we need to know it's length to add it to the leadOffset
  const maybeLeadingWhitespace = match[1];

  const matchingString = match[3];
  if (matchingString.length >= minMatchLength) {
    return {
      leadOffset: match.index + maybeLeadingWhitespace.length,
      matchingString,
      replaceableString: match[2],
    };
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 1);
}

class MentionTypeaheadOption extends MenuOption {
  name: string;
  picture: JSX.Element;
  userId: string;
  emailId: string;

  constructor(user: MentionUserType, picture: JSX.Element) {
    super(user.name || user.emailId || user.uniqueId);
    this.name = user.name || user.emailId || user.uniqueId;
    this.userId = user.id;
    this.picture = picture;
    this.emailId = user.emailId || '';
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  let className = cn(
    'item p-12 mt-2 min-w-[320px] rounded-8 cursor-pointer',
    isSelected && '!bg-bg-hover',
  );
  return (
    <Stack
      key={option.key}
      ref={option.setRefElement}
      aria-selected={isSelected}
      className={className}
      id={'typeahead-item-' + index}
      onClick={onClick}
      onMouseEnter={onMouseEnter}>
      {option.picture}
      <Stack className="ml-12" direction="col">
        <Typography variant="body-short-02">{option.name}</Typography>
        <Typography className="text-text-placeholder" variant="body-short-03">
          {option.emailId}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default function NewMentionsPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState<string | null>(null);

  const results = useMentionLookupService(queryString);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map(
          (user) => new MentionTypeaheadOption(user, <Avatar src={user?.profilePicture || ''} />),
        )
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.name, selectedOption.userId);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback((text: string) => {
    return getPossibleQueryMatch(text);
  }, []);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current
          ? ReactDOM.createPortal(
              <Box
                className="typeahead-popover mentions-menu"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  position: 'absolute',
                  zIndex: 10000,
                }}>
                <ul style={{ listStyle: 'none', margin: 0, padding: '8px' }}>
                  {options?.length > 0 ? (
                    options.map((option, i: number) => (
                      <MentionsTypeaheadMenuItem
                        key={option.key}
                        index={i}
                        isSelected={selectedIndex === i}
                        option={option}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                      />
                    ))
                  ) : (
                    <Stack className="p-12 min-w-[320px]">
                      <Typography variant="body-short-01">No results found</Typography>
                    </Stack>
                  )}
                </ul>
              </Box>,
              anchorElementRef.current,
            )
          : null
      }
      options={options}
      triggerFn={checkForMentionMatch}
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
    />
  );
}
